import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getActiveTaxNotice, type TaxNoticeRow } from "@/lib/taxNotice";

const DISMISS_KEY_PREFIX = "tax-notice-dismissed:";

function dismissKey(noticeId: string) {
  return `${DISMISS_KEY_PREFIX}${noticeId}`;
}

function formatEffectiveDate(iso: string | null): string {
  if (!iso) return "soon";
  try {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}

/**
 * Renders nothing unless an admin has explicitly published a tax-notice
 * announcement. Logged-in users only (mounted inside AppShell).
 */
export const TaxNoticeBanner = () => {
  const [notice, setNotice] = useState<TaxNoticeRow | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const n = await getActiveTaxNotice();
        if (cancelled) return;
        setNotice(n);
        if (n && typeof window !== "undefined") {
          setDismissed(window.localStorage.getItem(dismissKey(n.id)) === "1");
        }
      } catch {
        // silently ignore — banner is non-critical
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!notice || dismissed) return null;

  const handleDismiss = () => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(dismissKey(notice.id), "1");
    }
    setDismissed(true);
  };

  const dateStr = formatEffectiveDate(notice.effective_date);

  return (
    <div
      role="status"
      className={cn(
        "border-b border-amber-300/60 bg-amber-50 text-amber-900",
        "dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100",
      )}
    >
      <div className="mx-auto flex max-w-5xl items-start gap-3 px-4 py-2.5 text-sm">
        <div className="flex-1">
          <span className="font-semibold">{notice.title || "Upcoming billing change"}</span>
          {notice.body_md ? (
            <span className="ml-2 opacity-90">{notice.body_md}</span>
          ) : (
            <span className="ml-2 opacity-90">
              Applicable sales tax / VAT will be added to subscription invoices starting{" "}
              <strong>{dateStr}</strong>.
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 shrink-0 p-0 text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/40"
          aria-label="Dismiss tax notice"
          onClick={handleDismiss}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};


